/**
 * This file provides the implementation for the Polar Routes crusie search page.
 *
 * For development use:
 *
 * ```
 * cd /var/www/hydrogen/hydrogen-libs/nodejs/latinroutes/
 * npm run watch
 * ```
 */

import Vue from 'vue';
import Vuex from 'vuex';
import VuexPersist from 'vuex-persist'
import CruiseSearch from './cruise-search/CruiseSearch';

jQuery(function () {
	/**
	 * Denormalize data previously normalized with CruiseController::normalizeCruises().
	 *
	 * @param {Object} data JSON data from CruiseController.
	 *
	 * @return {Array} Returns an array of voyages.
	 */
	function denormalizeCruises(data) {
		denormalizeShips(data);
		denormalizeRoutes(data);

		for (let i = 0; i < data.cruises.length; ++i) {
			const cruise = data.cruises[i];
			cruise.ship = data.ships[cruise.ship];
			cruise.route = data.routes[cruise.route];

			// Voyages.
			for (let j = 0; j < cruise.voyages.length; ++j) {
				const voyage = cruise.voyages[j];

				for (let k = 0; k < voyage.offers.length; ++k) {
					const offerId = voyage.offers[k];
					const offer = data.offers[offerId];
					voyage.offers[k] = offer;
				}
			}
		}

		return data.cruises;
	}

	/**
	 * Denormalize data previously normalized with CruiseController::normalizeVoyages().
	 *
	 * @param {Object} data JSON data from CruiseController.
	 */
	function denormalizeShips(data) {
		const shipIds = Object.keys(data.ships);

		for (let i = 0; i < shipIds.length; ++i) {
			const shipId = shipIds[i];
			const ship = data.ships[shipId];
			ship.operator = data.operators[ship.operator];
		}
	}

	/**
	 * Denormalize data previously normalized with CruiseController::normalizeVoyages().
	 *
	 * @param {Object} data JSON data from CruiseController.
	 */
	function denormalizeRoutes(data) {
		const routeIds = Object.keys(data.routes);

		for (let i = 0; i < routeIds.length; ++i) {
			const routeId = routeIds[i];
			const route = data.routes[routeId];
			route.region = data.regions[route.region];
		}
	}

	/**
	 * The API returns a result for each route and ship pair, but they
	 * want to display results grouped by route.
	 *
	 * @param {Array} cruises The search results returned from the API.
	 *
	 * @return {Array} Returns the search results grouped by route.
	 */
	function createResults(cruises) {
		// A temporary map of route ID => results, for grouping.
		const resultsMap = {};

		// An array of results.
		const results = [];

		for (let i = 0; i < cruises.length; ++i)
		{
			const cruise = cruises[i];
			const routeId = cruise.route.id;

			if (typeof resultsMap[routeId] == 'undefined') {
				const result = { cruises: [] };
				resultsMap[routeId] = result;
				results.push(result);
			}

			resultsMap[routeId].cruises.push(cruise);
		}

		return results;
	}

	/**
	 * Sort the results.
	 *
	 * @param {Result} a The first item to compare.
	 * @param {Result} b The second item to compare.
	 * @param {String} sort The sorting criteria, either 'recommended' or 'price'.
	 */
	function sortResults(a, b, sort) {
		// Just compare the first cruise in each result.
		a = a.cruises[0];
		b = b.cruises[0];

		switch (sort) {
			case 'recommended':
				const keyOperators = [2, 3, 10];
				const preferredOperators = [1, 9, 12, 22];

				const getOperatorTier = (operatorId) => {
					if (keyOperators.includes(operatorId))
						return 2;

					if (preferredOperators.includes(operatorId))
						return 1;

					return 0;
				};

				const aTier = getOperatorTier(a.ship.operator.id);
				const bTier = getOperatorTier(b.ship.operator.id);

				if (aTier !== bTier) {
					return bTier - aTier;
				}

				// If they are both recommended or not, sort alphabetically
				return a.route.name < b.route.name ? -1 : 1;

			case 'price':
				if (a.minimumPrice == b.minimumPrice)
					return 0;

				return a.minimumPrice < b.minimumPrice ? -1 : 1;

			default:
				throw new Error('Invalid sort order: ' + sort);
		}
	}


	Vue.use(Vuex);

	// Persist the selected filter values in the session
	const vuexPersist = new VuexPersist({
		key: 'cruise-search/' + window.location.href,
		storage: sessionStorage,
		filter: mutation => mutation.type === 'setSelectedFilterValues' || mutation.type === 'setSort',
		// Only store the selectedFilterValues and sort so that everything else is reset on page load
		reducer: (state) => ({selectedFilterValues: state.selectedFilterValues, sort: state.sort}),
	});

	// Create the Vuex data store.
	let store = new Vuex.Store({
		plugins: [vuexPersist.plugin],

		// Data.
		state: {
			// The URL of the search API.
			apiUrl: '/cruise-search-ajax/',

			// Filter values that the user may select.
			availableFilterValues: {},

			// Filter values that the user has selected.
			selectedFilterValues: {},

			// Search results returned from the API.
			cruises: [],

			// True when an AJAX call is in progress.
			loading: false,

			// The sort order.
			sort: 'recommended',

			// Whether or not to show the search filters.
			showFilters: true,
		},

		// Computed properties.
		getters: {
			/**
			 * Get the available filters that can be used to narrow the search
			 * and still yield results.
			 *
			 * @param {Store} store The Vuex store.
			 *
			 * @return {Object} Returns an object with keys for each filter, and values of
			 *         each value available for that filter.
			 */
			availableFilterValues: function (store) {
				return store.availableFilterValues;
			},

			/**
			 * Get the filter values that have been selected by the user.
			 *
			 * @param {Store} store The Vuex store.
			 *
			 * @return {Object} Returns an object with keys for each filter, and values
			 *         of each value selected by the user.
			 */
			selectedFilterValues: function (store) {
				return store.selectedFilterValues;
			},

			/**
			 * Get the raw search results.
			 *
			 * @param {Store} store The Vuex store.
			 *
			 * @return {Array} Returns an array of cruises from the search AJAX callback.
			 */
			cruises: function (store) {
				return store.cruises;
			},

			/**
			 * Get the formatted search results.
			 *
			 * @param {Store} store The Vuex store.
			 *
			 * @return {Array} Returns an array of results from createResults().
			 */
			results: function (store) {
				const results = createResults(store.cruises);
				results.sort(function (a, b) { return sortResults(a, b, store.sort); });
				return results;
			},

			/**
			 * Get whether an AJAX call is in progress.
			 *
			 * @param {Store} store The Vuex store.
			 *
			 * @return {Boolean} Returns true if an AJAX call is in progress, false otherwise.
			 */
			loading: function (store) {
				return store.loading;
			},

			/**
			 * Get the sort order for search results.
			 *
			 * @param {Store} store The Vuex store.
			 *
			 * @return {String} Returns the sort order, either 'recommended' or 'price'.
			 */
			sort: function (store) {
				return store.sort;
			},

			/**
			 * Get whether or not to show the filters.
			 *
			 * @param {Store} store The Vuex store.
			 *
			 * @return {Boolean} Returns true to show the filters, false to hide them.
			 */
			showFilters: function (store) {
				return store.showFilters;
			}
		},

		// Methods that do not change the state.
		actions: {
			/**
			 * Query the Cruise Search API.
			 *
			 * The filter values selected by the user are passed to the cruise search AJAX
			 * callback, and the results are stored in `store.results`.
			 *
			 * @param {*} context The Vuex context.
			 */
			async search(context) {
				context.commit('setLoading', true);
				let ajaxData = context.getters.selectedFilterValues;

				const result = await jQuery.ajax(context.state.apiUrl + '?action=search', { data: ajaxData });

				if (!result.success)
					throw new Error('Search failed: ' + result.message);

				let cruises = null;

				// Convert IDs back into objects.
				cruises = denormalizeCruises(result.cruises);

				context.dispatch('setCruises', cruises);
				context.commit('setAvailableFilterValues', result.availableFilterValues);
				context.commit('setLoading', false);
			},

			/**
			 * Store the filter values that have been selected by the user.
			 *
			 * This also triggers a search AJAX call.
			 *
			 * @param {*} context The Vuex context.
			 * @param {*} filter The filter to set, as an object of: {name: filter name string, value: filter values array}.
			 */
			setSelectedFilterValues(context, filter) {
				context.commit('setSelectedFilterValues', filter);
				context.dispatch('search');
			},

			/**
			 * Set whether an AJAX call is in progress.
			 *
			 * @param {*} context The Vuex context.
			 * @param {Boolean} loading True if an AJAX call is in progress.
			 */
			setLoading(context, loading) {
				context.commit('setLoading', loading);
			},

			/**
			 * Set the sort order for search results.
			 *
			 * @param {*} context The Vuex context.
			 * @param {String} sort The sort order, either 'recommended' or 'price'.
			 */
			setSort(context, sort) {
				context.commit('setSort', sort);
			},

			/**
			 * Set the search results.
			 *
			 * @param {*} context The Vuex context.
			 * @param {Array} cruises An array of search results.
			 */
			setCruises(context, cruises) {
				// Fill in the minimum price for each result.
				for (let r = 0; r < cruises.length; ++r) {
					const cruise = cruises[r];

				let minPrice = cruise.voyages.reduce((minPrice, voyage) => Math.min(minPrice, voyage.fromPrice), Number.MAX_SAFE_INTEGER);

				cruise.minimumPrice = minPrice === Number.MAX_SAFE_INTEGER ? 0 : minPrice;
				}

				context.commit('setCruises', cruises);
			},
		},

		// Methods that change the state.
		mutations: {
			/**
			 * Set the raw results returned by the AJAX callback.
			 *
			 * @param {State} state The Vuex state.
			 * @param {Array} results An array of cruise search results returned by the AJAX callback.
			 */
			setCruises(state, cruises) {
				state.cruises = cruises;
			},

			/**
			 * Store the available filter values that will return search results.
			 *
			 * Example of availableFilterValues:
			 *
			 * ```
			 * {
			 *     operatorIds: [1, 2],
			 *     shipIds: [1, 2, 3],
			 * }
			 * ```
			 *
			 * @param {State} state The Vuex state.
			 * @param {Object} availableFilterValues The filter values that the user may choose.
			 */
			setAvailableFilterValues(state, availableFilterValues) {
				state.availableFilterValues = availableFilterValues;
			},

			/**
			 * Store the filter values that the user has selected.
			 *
			 * This only sets the filter values for a single filter,
			 * e.g. for `shipIds`.
			 *
			 * The `filter` parameter is an object of:
			 *
			 * ```
			 * {
			 *     name: 'shipIds',
			 *     value: [1, 2, 3],
			 * }
			 * ```
			 *
			 * @param {State} state The Vuex state.
			 * @param {Object} filter The filter value, {name, value}.
			 */
			setSelectedFilterValues(state, filter) {
				Vue.set(state.selectedFilterValues, filter.name, filter.value);
			},

			/**
			 * Set whether an AJAX call is in progress.
			 *
			 * @param {State} state The Vuex state.
			 * @param {Boolean} loading True if an AJAX call is in progress, false otherwise.
			 */
			setLoading(state, loading) {
				state.loading = loading;
			},

			/**
			 * Set the sort order for results.
			 *
			 * @param {State} state The Vuex state.
			 * @param {String} sort The sort order, either 'recommended' or 'price'.
			 */
			setSort(state, sort) {
				state.sort = sort;
			},
		}
	});

	// Initialize the Vue application.
	const cruiseSearchApp = new Vue({
		el: '#cruise-search-app',
		store,
		components: {
			CruiseSearch,
		},
		mounted: function () {
			// Apply pre-selected filters from the query string.
			const queryParameters = parseQueryString(window.location.search);

			if (queryParameters && queryParameters.gclid)
				delete queryParameters.gclid;

			// Apply pre-selected filters from javascript global `cruiseSearchConstraints`.
			// This will overwrite filters with the same name in the query string.
			if (typeof cruiseSearchConstraints !== 'undefined')
				Object.assign(queryParameters, cruiseSearchConstraints);

			const queryParameterKeys = Object.keys(queryParameters);

			for (let i = 0; i < queryParameterKeys.length; ++i) {
				const queryParameterKey = queryParameterKeys[i];
				const filter = { name: queryParameterKey, value: queryParameters[queryParameterKey] };
				this.$store.commit('setSelectedFilterValues', filter);
			}

			// Do an AJAX call as soon as the page has loaded
			// to populate the initial results.
			this.$store.dispatch('search');
		}
	})
});

// Polyfill from  https://github.com/MaxArt2501/base64-js/blob/master/base64.js
(function() {
	// base64 character set, plus padding character (=)
	var b64 = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/=",

		// Regular expression to check formal correctness of base64 encoded strings
		b64re = /^(?:[A-Za-z\d+\/]{4})*?(?:[A-Za-z\d+\/]{2}(?:==)?|[A-Za-z\d+\/]{3}=?)?$/;

	window.btoa = window.btoa || function(string) {
		string = String(string);
		var bitmap, a, b, c,
			result = "",
			i = 0,
			rest = string.length % 3; // To determine the final padding

		for (; i < string.length;) {
			if ((a = string.charCodeAt(i++)) > 255 ||
				(b = string.charCodeAt(i++)) > 255 ||
				(c = string.charCodeAt(i++)) > 255)
				throw new TypeError("Failed to execute 'btoa' on 'Window': The string to be encoded contains characters outside of the Latin1 range.");

			bitmap = (a << 16) | (b << 8) | c;
			result += b64.charAt(bitmap >> 18 & 63) + b64.charAt(bitmap >> 12 & 63) +
				b64.charAt(bitmap >> 6 & 63) + b64.charAt(bitmap & 63);
		}

		// If there's need of padding, replace the last 'A's with equal signs
		return rest ? result.slice(0, rest - 3) + "===".substring(rest) : result;
	};
})();
